'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Download, FileText, RefreshCw, ExternalLink, BookOpen, CheckCircle, ChevronRight, Shield } from 'lucide-react';
import Link from 'next/link';

interface Document {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  category: string;
  pages: string;
  year: string;
  source: string;
  sourceUrl: string;
  tags: string[];
  icon: React.ElementType;
  color: string;
  bgColor: string;
  highlights: string[];
  relevance: string;
  isPrincipal?: boolean;
}

const documents: Document[] = [
  {
    id: 'lra-9circle',
    title: 'LRA 9-Circle Property Tax Methodology',
    subtitle: 'Principal Reference Document — LRA Property Tax Digital Intelligence System',
    description: 'The definitive and principal reference document for the LRA Property Tax Digital Intelligence System\'s 9-stage cycle. This framework explains how LRA Nexus handles every stage from Property Discovery (Identification), Mapping and Registration through Property Enforcement, with international proof points, IMF benchmarks, and Africa comparison data.',
    category: 'LRA Nexus',
    pages: '92 pages',
    year: '2025',
    source: 'Liberia Revenue Authority — Digital Transformation Office',
    sourceUrl: '/nine-circle-methodology',
    tags: ['Property Tax', '9-Circle Cycle', 'LRA Nexus', 'Methodology', 'IMF Standards'],
    icon: RefreshCw,
    color: '#d4a017',
    bgColor: 'rgba(212,160,23,0.1)',
    highlights: [
      'All 9 stages of the Property Tax Cycle explained',
      'How LRA Nexus handles each stage end-to-end',
      'Africa proof points: 6 countries, $497M+ invested',
      'IMF benchmark comparisons',
      'Liberia revenue gap analysis: $19.26M/year',
      'Implementation timeline and cost projections',
    ],
    relevance: 'This is the primary and principal reference document for the LRA Property Tax Digital Intelligence System. It demonstrates that every stage of the property tax cycle is handled by a dedicated, proven module aligned with international standards — the authoritative foundation for all LRA Nexus contract presentations.',
    isPrincipal: true,
  },
  {
    id: 'imf-fiscal',
    title: 'IMF Fiscal Monitor: Property Tax Reform in Sub-Saharan Africa',
    subtitle: 'Revenue Potential and Implementation Pathways',
    description: 'The IMF\'s analysis of property tax reform opportunities across Sub-Saharan Africa, with specific focus on countries with property tax/GDP ratios below 0.5%. Liberia is identified as having the highest-leverage fiscal reform opportunity in the region.',
    category: 'IMF',
    pages: '64 pages',
    year: '2023',
    source: 'International Monetary Fund — Fiscal Affairs Department',
    sourceUrl: 'https://www.imf.org/en/Publications/FM',
    tags: ['IMF', 'Fiscal Policy', 'Property Tax', 'Sub-Saharan Africa', 'Revenue Reform'],
    icon: BookOpen,
    color: '#34d399',
    bgColor: 'rgba(52,211,153,0.1)',
    highlights: [
      'Liberia property tax/GDP at 0.1% vs 0.5% IMF minimum',
      'Revenue potential: $24.4M/year at full registration',
      'Peer comparison: Rwanda 0.62%, Kenya 0.48%, Ghana 0.31%',
      'Digital systems as the primary reform lever',
      'Property registration as the critical bottleneck',
      'Recommended implementation sequencing for Liberia',
    ],
    relevance: 'The IMF directly identifies Liberia\'s property tax gap as the highest-leverage fiscal reform available. This document provides the international authority backing for LRA Nexus\'s revenue projections.',
  },
  {
    id: 'liberia-public-finance',
    title: 'Liberia Public Finance & Property Tax Capacity Review',
    subtitle: 'Revenue Mobilization and Digital Transformation Assessment',
    description: 'A comprehensive assessment of Liberia\'s public finance capacity, with detailed analysis of the property tax system, registration gaps, and recommendations for digital transformation. This review directly informed the design of LRA Nexus and provides the authoritative baseline data for the system.',
    category: 'LRA Reference',
    pages: '112 pages',
    year: '2022',
    source: 'Liberia Revenue Authority — Policy & Research Division',
    sourceUrl: '/nine-circle-methodology',
    tags: ['Liberia', 'Public Finance', 'Revenue Mobilization', 'Property Tax', 'Capacity Building'],
    icon: Shield,
    color: '#a78bfa',
    bgColor: 'rgba(167,139,250,0.1)',
    highlights: [
      'Only 11% of taxable properties currently registered in Liberia',
      '350,000–500,000 estimated taxable properties nationwide',
      'Property tax collection: $5.14M vs $24.4M potential',
      'Digital cadastre as the primary reform recommendation',
      'County-level capacity assessment and gap analysis',
      'Estimated 3–5 year implementation timeline for full system',
    ],
    relevance: 'This review provides the authoritative baseline data used throughout LRA Nexus — including the 11% registration rate, $5.14M collection figure, and $24.4M revenue potential.',
  },
];

export default function ResourcesClient() {
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);

  const handleDownload = (doc: Document) => {
    if (doc.sourceUrl.startsWith('http')) {
      window.open(doc.sourceUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = doc.sourceUrl;
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Link href="/" className="text-xs hover:opacity-80 transition-opacity" style={{ color: 'var(--dark-muted)' }}>
              Dashboard
            </Link>
            <ChevronRight size={12} style={{ color: 'var(--dark-muted)' }} />
            <span className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>Resources & Downloads</span>
          </div>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, rgba(212,160,23,0.2) 0%, rgba(96,165,250,0.2) 100%)', border: '1px solid rgba(212,160,23,0.3)' }}>
                <Download size={22} style={{ color: '#d4a017' }} />
              </div>
              <div>
                <h1 className="text-2xl font-black" style={{ color: 'var(--dark-text)' }}>
                  Resources & Downloads
                </h1>
                <p className="text-sm mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                  LRA 9-Circle Property Tax Methodology · IMF Benchmarks · Reference Documents
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Principal Reference Banner */}
        <div className="mb-6 rounded-2xl p-4 flex items-center gap-4"
          style={{
            background: 'linear-gradient(135deg, rgba(212,160,23,0.12) 0%, rgba(212,160,23,0.04) 100%)',
            border: '1px solid rgba(212,160,23,0.35)',
          }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(212,160,23,0.2)', border: '1px solid rgba(212,160,23,0.4)' }}>
            <RefreshCw size={18} style={{ color: '#d4a017' }} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color: '#d4a017' }}>Principal Reference Document</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
              LRA 9-Circle Property Tax Methodology is the principal reference document for the LRA Property Tax Digital Intelligence System
            </p>
          </div>
        </div>

        {/* Summary bar */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Reference Documents', value: '3', color: '#60a5fa' },
            { label: 'International Sources', value: 'IMF · LRA', color: '#34d399' },
            { label: 'Africa Countries Cited', value: '6 Nations', color: '#fbbf24' },
            { label: 'Combined Investment', value: '$497M+', color: '#f87171' },
          ].map((s) => (
            <div key={s.label} className="rounded-xl px-4 py-3" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{s.label}</p>
              <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Document Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {documents.map((doc) => {
            const DocIcon = doc.icon;
            const isSelected = selectedDoc === doc.id;
            return (
              <div key={doc.id} className={`rounded-2xl overflow-hidden transition-all duration-200 ${doc.isPrincipal ? 'lg:col-span-2' : ''}`}
                style={{
                  backgroundColor: 'var(--dark-card)',
                  border: `1px solid ${isSelected ? doc.color + '50' : doc.isPrincipal ? doc.color + '40' : 'var(--dark-border)'}`,
                  boxShadow: doc.isPrincipal ? `0 0 24px ${doc.color}12` : isSelected ? `0 0 20px ${doc.color}15` : 'none',
                }}>
                {/* Card Header */}
                <div className="p-5" style={{ borderBottom: '1px solid var(--dark-border)', background: doc.bgColor }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: doc.bgColor, border: `1px solid ${doc.color}30` }}>
                        <DocIcon size={18} style={{ color: doc.color }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: doc.bgColor, color: doc.color, border: `1px solid ${doc.color}30` }}>
                            {doc.category}
                          </span>
                          {doc.isPrincipal && (
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: 'rgba(212,160,23,0.2)', color: '#d4a017', border: '1px solid rgba(212,160,23,0.4)' }}>
                              ★ Principal Reference
                            </span>
                          )}
                          <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{doc.year} · {doc.pages}</span>
                        </div>
                        <h3 className="text-sm font-bold leading-tight" style={{ color: 'var(--dark-text)' }}>{doc.title}</h3>
                        <p className="text-xs mt-0.5" style={{ color: doc.color }}>{doc.subtitle}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5">
                  <p className="text-xs leading-relaxed mb-4" style={{ color: 'var(--dark-muted)' }}>{doc.description}</p>

                  {/* Key Highlights */}
                  <div className="mb-4">
                    <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-text)' }}>Key Highlights</p>
                    <div className={`${doc.isPrincipal ? 'grid grid-cols-1 md:grid-cols-2 gap-1.5' : 'space-y-1.5'}`}>
                      {doc.highlights.map((h, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle size={12} className="flex-shrink-0 mt-0.5" style={{ color: doc.color }} />
                          <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{h}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Relevance */}
                  <div className="rounded-xl p-3 mb-4" style={{ backgroundColor: doc.bgColor, border: `1px solid ${doc.color}20` }}>
                    <p className="text-xs font-semibold mb-1" style={{ color: doc.color }}>
                      {doc.isPrincipal ? 'Principal Reference — LRA Property Tax Digital Intelligence System' : 'Why This Matters for LRA Nexus'}
                    </p>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-muted)' }}>{doc.relevance}</p>
                  </div>

                  {/* Source & Actions */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium" style={{ color: 'var(--dark-text)' }}>{doc.source}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedDoc(isSelected ? null : doc.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                        style={{
                          backgroundColor: 'var(--dark-elevated)',
                          color: 'var(--dark-muted)',
                          border: '1px solid var(--dark-border)',
                        }}>
                        <FileText size={12} />
                        {isSelected ? 'Less' : 'Details'}
                      </button>
                      <button
                        onClick={() => handleDownload(doc)}
                        className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
                        style={{ backgroundColor: doc.color, color: '#070e1f' }}>
                        {doc.sourceUrl.startsWith('http') ? (
                          <><ExternalLink size={12} /> Open Source</>
                        ) : (
                          <><ExternalLink size={12} /> View in Platform</>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Tags */}
                  {isSelected && (
                    <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--dark-border)' }}>
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>Topics Covered</p>
                      <div className="flex flex-wrap gap-1.5">
                        {doc.tags.map((tag) => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Presentation Note */}
        <div className="mt-6 rounded-2xl p-5" style={{
          background: 'linear-gradient(135deg, rgba(212,160,23,0.08) 0%, rgba(96,165,250,0.06) 100%)',
          border: '1px solid rgba(212,160,23,0.2)'
        }}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'rgba(212,160,23,0.15)' }}>
              <BookOpen size={16} style={{ color: 'var(--accent)' }} />
            </div>
            <div>
              <p className="text-sm font-bold mb-1" style={{ color: 'var(--dark-text)' }}>Presentation Reference Note</p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
                The <strong style={{ color: '#d4a017' }}>LRA 9-Circle Property Tax Methodology</strong> is the principal reference document for the LRA Property Tax Digital Intelligence System and serves as the primary foundation for all contract presentations. 
                Reference the IMF Fiscal Monitor to justify revenue projections, and the LRA 9-Circle Methodology to demonstrate that every stage of the property tax cycle is handled by a dedicated, proven module. 
                The combined $497M+ Africa investment figure positions LRA Nexus as the cost-effective alternative to building from scratch.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
